import React, { useMemo, useState } from 'react';
import { Globe, X, ExternalLink } from 'lucide-react';
import { InlineMath, BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import 'katex/dist/contrib/mhchem.js'; // enables \ce{...} chemistry rendering
import { parseBlocks } from '../lib/richEditor/blocks';

/**
 * Renders text content with basic formatting + native math/chemistry support.
 *  - Block math:    $$ ... $$       or  \[ ... \]
 *  - Inline math:   $ ... $          or  \( ... \)
 *  - Chemistry:     \ce{...}         (recognised inside any math span)
 *  - Inline ref:    [ref:N]          (resolved against `references` prop)
 * Falls back to plain rendering if KaTeX cannot parse.
 */

// Counts non-overlapping occurrences of `needle` inside `haystack`. Shared
// between highlight creation (FrameworkSection's handleTextSelect, which
// records which occurrence the teacher selected) and highlight rendering
// (wrapHighlights below, which must count the same way to find it again).
export function countOccurrences(haystack, needle) {
  if (!needle) return 0;
  let count = 0;
  let idx = 0;
  while ((idx = haystack.indexOf(needle, idx)) !== -1) {
    count += 1;
    idx += needle.length;
  }
  return count;
}

// Match math + ref-marker tokens together so we can interleave them
// inside a paragraph without losing order. Each alternation = one token:
//   1) $$…$$ block             2) \[…\] block
//   3) \(…\) inline math       4) $…$ inline math
//   5) [ref:N] reference chip
const TOKEN_REGEX = /(\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\]|\\\([^\n]+?\\\)|\$[\s\S]+?\$|\[ref:\d+\])/g;
const REF_REGEX = /^\[ref:(\d+)\]$/;

function normalizeMathString(s) {
  if (!s) return '';
  let out = String(s);
  out = out.replace(/\\\\/g, '\\');
  out = out.replace(/[\t\r\n]+/g, ' ');
  out = out.replace(/\s{2,}/g, ' ');
  return out.trim();
}

function MathOrText({ token, body }) {
  if (!token) return null;
  // Block
  if (token.startsWith('$$') && token.endsWith('$$')) {
    const inner = normalizeMathString(token.slice(2, -2));
    try { return <BlockMathCard latex={inner} />; }
    catch { return <code className="text-rose-600 text-xs">[math error] {inner}</code>; }
  }
  if (token.startsWith('\\[') && token.endsWith('\\]')) {
    const inner = normalizeMathString(token.slice(2, -2));
    try { return <BlockMathCard latex={inner} />; }
    catch { return <code className="text-rose-600 text-xs">[math error] {inner}</code>; }
  }
  // Inline (\(...\))
  if (token.startsWith('\\(') && token.endsWith('\\)')) {
    const inner = normalizeMathString(token.slice(2, -2));
    try { return <InlineMathChip math={inner} />; }
    catch { return <code className="text-rose-600 text-xs">{inner}</code>; }
  }
  // Inline ($...$)
  if (token.startsWith('$') && token.endsWith('$')) {
    const inner = normalizeMathString(token.slice(1, -1));
    try { return <InlineMathChip math={inner} />; }
    catch { return <code className="text-rose-600 text-xs">{inner}</code>; }
  }
  return body;
}

/**
 * Inline math chip — subtle tinted background so a `$f(x)$` inside prose
 * pops without breaking the line. Hover reveals the LaTeX source as a
 * native tooltip so power-users can copy-edit on the spot.
 */
// KaTeX has no \mspace (amsmath) but supports \mskip with the same {Nmu}
// argument -- the ingested JEE/NEET question bank uses \mspace{2mu}, which
// otherwise renders as red error text. react-katex exposes no `macros`
// option, so normalise the source string instead.
const normalizeLatex = (latex) => String(latex).replace(/\\mspace(?![a-zA-Z])/g, '\\mskip');

function InlineMathChip({ math }) {
  return (
    <span
      className="inline-math-chip"
      title={math}
      style={{
        padding: '1px 6px',
        margin: '0 1px',
        borderRadius: '6px',
        background: 'color-mix(in srgb, #0d9488 8%, transparent)',
        color: 'inherit',
      }}>
      <InlineMath math={normalizeLatex(math)} />
    </span>
  );
}

/**
 * Block math — a standalone centered block with overflow-x-auto so wide
 * equations never break the layout. Deliberately chrome-free (no tinted
 * card, no copy button): the formula reads as part of the prose, just on
 * its own line. Used for every `$$…$$` / `\[…\]` token that lands on its
 * own paragraph.
 */
function BlockMathCard({ latex }) {
  return (
    <span
      className="block-math-card"
      style={{
        display: 'block',
        margin: '14px auto',
        overflowX: 'auto',
        textAlign: 'center',
      }}>
      <BlockMath math={normalizeLatex(latex)} />
    </span>
  );
}

// In-app link frame — opens a reference URL inside the app in an iframe
// overlay (same pattern as the attachment viewer) instead of a new browser
// tab, which the embedded webview can't spawn. Sites that refuse to be
// framed (X-Frame-Options / CSP) still have the explicit "Open in new tab"
// escape hatch in the header.
export function LinkFrameOverlay({ url, title, onClose }) {
  return (
    <span className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-4 not-italic font-normal text-left" onClick={(e) => { e.stopPropagation(); onClose(); }} data-testid="reference-link-frame">
      <span className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[92vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <span className="flex items-center gap-3 px-5 py-3 border-b border-gray-200 bg-gradient-to-r from-white to-gray-50">
          <span className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm" style={{ background: 'color-mix(in srgb, #0d9488 12%, transparent)', color: '#0d9488' }}>
            <Globe className="w-5 h-5" />
          </span>
          <span className="flex-1 min-w-0 block">
            <span className="block text-[14px] font-bold text-gray-800 truncate">{title}</span>
            <span className="block text-[10px] text-gray-500 truncate">{url}</span>
          </span>
          <a href={url} target="_blank" rel="noopener noreferrer" className="text-[11px] font-bold text-white bg-[#0d9488] hover:bg-[#0f766e] px-3 py-2 rounded-lg flex items-center gap-1.5 transition-colors" data-testid="reference-link-frame-newtab"><ExternalLink className="w-3.5 h-3.5" /> Open in new tab</a>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100" data-testid="reference-link-frame-close"><X className="w-5 h-5" /></button>
        </span>
        <iframe src={url} title={title} className="w-full flex-1 bg-white" sandbox="allow-scripts allow-same-origin allow-forms allow-popups" data-testid="reference-link-frame-iframe" />
      </span>
    </span>
  );
}

// Inline reference chip — renders a small clickable badge for `[ref:N]`.
// Renders gracefully (greyed-out plain text) if the resolved reference is
// missing, so the prose never breaks for a published topic whose
// references list was edited later.
function RefChip({ index, references }) {
  const [frameOpen, setFrameOpen] = useState(false);
  const ref = references && references[index - 1];
  if (!ref) {
    return (
      <span className="inline-flex items-center text-[10px] font-mono align-baseline opacity-50">[{index}]</span>
    );
  }
  const label = (ref.title || ref.url || `Reference ${index}`).toString();
  const isLessonRef = (ref.url || '').startsWith('lesson://') || ref.kind === 'lesson';
  if (isLessonRef) {
    return (
      <span
        className="inline-flex items-baseline gap-0.5 align-baseline text-[12px] font-medium px-1.5 py-0 rounded mx-px"
        style={{ background: 'color-mix(in srgb, #6366f1 12%, transparent)', color: '#4f46e5' }}
        title={label}>
        <sup className="text-[9px] font-mono opacity-70">{index}</sup>
      </span>
    );
  }
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (ref.url) setFrameOpen(true); }}
        className="inline-flex items-baseline gap-0.5 align-baseline text-[12px] font-medium px-1.5 py-0 rounded mx-px transition-colors hover:underline cursor-pointer"
        style={{ background: 'color-mix(in srgb, #0d9488 12%, transparent)', color: '#0d9488' }}
        title={`${label} — ${ref.url || ''}`}
        data-testid={`inline-ref-${index}`}>
        <sup className="text-[9px] font-mono opacity-80">{index}</sup>
      </button>
      {frameOpen && ref.url && (
        <LinkFrameOverlay url={ref.url} title={label} onClose={() => setFrameOpen(false)} />
      )}
    </>
  );
}

// Light markdown clean-up
function softCleanInline(line) {
  return line
    .replace(/\*\*\*(.*?)\*\*\*/g, '$1')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '');
}

// Markdown-mode token regex: emphasis/code spans are tried BEFORE math/ref,
// unlike TOKEN_REGEX above. Splitting math out first (as TOKEN_REGEX does)
// tears a `**...$A$...**` span into separate text chunks whose `**` markers
// land in different chunks and can no longer pair up — they'd render as
// literal asterisks around rendered math instead of bolding it. Listing
// emphasis first here makes it win: JS regex alternation takes the first
// alternative that matches at a given start position, so a `**` starting a
// bold run is captured whole (span content may include `$...$`) before the
// math alternatives ever get a chance to slice it up.
const MIXED_TOKEN_REGEX = /(\*\*\*[^*\n]+\*\*\*|\*\*[^*\n]+\*\*|\*[^*\n]+\*|`[^`\n]+`|\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\]|\\\([^\n]+?\\\)|\$[^\n$]+?\$|\[ref:\d+\])/g;

// Split a paragraph into [{type:'text'|'math'|'ref'|'mdbold'|'mditalic'|
// 'mdbolditalic'|'mdcode', value}], preserving order. Non-markdown mode only
// tokenizes math/ref and soft-strips any markdown markers found (unchanged
// legacy behavior). Markdown mode also tokenizes emphasis/code spans whole,
// so a span's content — including any inline math nested inside — renders
// as one unit instead of being torn apart (see renderTokens).
function tokenize(paragraph, markdown = false) {
  if (!paragraph) return [];
  if (!markdown) {
    // Fast-path: no math/ref markers anywhere → single text token.
    if (!paragraph.includes('$') && !paragraph.includes('\\[')
        && !paragraph.includes('\\(') && !paragraph.includes('[ref:')) {
      return [{ type: 'text', value: softCleanInline(paragraph) }];
    }
    const out = [];
    let last = 0;
    paragraph.replace(TOKEN_REGEX, (match, _g, idx) => {
      if (idx > last) out.push({ type: 'text', value: softCleanInline(paragraph.slice(last, idx)) });
      const refMatch = REF_REGEX.exec(match);
      if (refMatch) out.push({ type: 'ref', value: parseInt(refMatch[1], 10) });
      else out.push({ type: 'math', value: match });
      last = idx + match.length;
      return match;
    });
    if (last < paragraph.length) out.push({ type: 'text', value: softCleanInline(paragraph.slice(last)) });
    return out;
  }

  if (!/[*`$]|\\\[|\\\(|\[ref:/.test(paragraph)) {
    return [{ type: 'text', value: paragraph }];
  }
  const out = [];
  let last = 0;
  paragraph.replace(MIXED_TOKEN_REGEX, (match, _g, idx) => {
    if (idx > last) out.push({ type: 'text', value: paragraph.slice(last, idx) });
    const refMatch = REF_REGEX.exec(match);
    if (refMatch) {
      out.push({ type: 'ref', value: parseInt(refMatch[1], 10) });
    } else if (match.startsWith('$$') || match.startsWith('\\[') || match.startsWith('\\(') || match.startsWith('$')) {
      out.push({ type: 'math', value: match });
    } else if (match.startsWith('***')) {
      out.push({ type: 'mdbolditalic', value: match.slice(3, -3) });
    } else if (match.startsWith('**')) {
      out.push({ type: 'mdbold', value: match.slice(2, -2) });
    } else if (match.startsWith('`')) {
      out.push({ type: 'mdcode', value: match.slice(1, -1) });
    } else {
      out.push({ type: 'mditalic', value: match.slice(1, -1) });
    }
    last = idx + match.length;
    return match;
  });
  if (last < paragraph.length) out.push({ type: 'text', value: paragraph.slice(last) });
  return out;
}

function renderTokens(tokens, baseKey, wrapText, references, markdown = false) {
  return tokens.map((t, i) => {
    const key = `${baseKey}-t-${i}`;
    if (t.type === 'math') return <MathOrText key={`${baseKey}-m-${i}`} token={t.value} body={t.value} />;
    if (t.type === 'ref') return <RefChip key={`${baseKey}-r-${i}`} index={t.value} references={references} />;
    if (t.type === 'mdcode') return <code key={key} className="px-1 py-0.5 rounded bg-black/5 text-[12px] font-mono">{t.value}</code>;
    if (t.type === 'mdbold' || t.type === 'mditalic' || t.type === 'mdbolditalic') {
      // The outer split already isolated the emphasis run whole (including
      // any inline math inside it) — re-tokenize just its contents for
      // math/ref, not emphasis again, so nested `**` can't recurse.
      const inner = renderTokens(tokenize(t.value, false), key, wrapText, references, false);
      if (t.type === 'mdbold') return <strong key={key}>{inner}</strong>;
      if (t.type === 'mdbolditalic') return <strong key={key}><em>{inner}</em></strong>;
      return <em key={key}>{inner}</em>;
    }
    const node = wrapText ? wrapText(t.value, key) : t.value;
    return <React.Fragment key={key}>{node}</React.Fragment>;
  });
}

// Content authored/edited via the new block-based rich text editor
// (frontend/src/components/teacher/ContentEditor/richEditor/) uses inline
// tag tokens (<b>, <i>, ...) and block-line prefixes (#, -, 1., >) the
// legacy path above does not understand (it strips/flattens them). Either
// signal routes the WHOLE text through the block-aware renderer below;
// content with neither renders through the untouched legacy path further
// down — this is a purely additive branch, not a rewrite.
const NEW_SCHEME_RE = /<\/?(?:b|i|u|s|code|eq)>|<(?:c|hl):[a-z]+>|^(?:#{1,4}\s|[-*+]\s|\d+\.\s|>\s)/m;

function isNewSchemeContent(text) {
  return NEW_SCHEME_RE.test(text);
}

// Splits text into non-overlapping runs the same way segmentsFromMarks
// does, but keeps the raw active Mark[] per run instead of a merged style
// object — read mode needs real semantic tags (<strong>/<em>/...), not
// inline CSS, so each mark kind maps to its own wrapping element below.
function segmentsWithActiveMarks(text, marks) {
  if (!text) return [];
  if (!marks.length) return [{ text, active: [] }];
  const cuts = new Set([0, text.length]);
  marks.forEach((m) => { cuts.add(m.start); cuts.add(m.end); });
  const points = Array.from(cuts).sort((a, b) => a - b);
  const segments = [];
  for (let i = 0; i < points.length - 1; i++) {
    const s = points[i];
    const e = points[i + 1];
    if (s >= e) continue;
    const active = marks.filter((m) => m.start <= s && m.end >= e);
    segments.push({ text: text.slice(s, e), active });
  }
  return segments;
}

// Combines a block's plain text + marks (bold/italic/etc ranges) with the
// existing math/ref TOKEN_REGEX tokenizer: marks give the outer semantic
// wrapping, tokenize() finds math/ref tokens within each run. Reuses
// tokenize/renderTokens/MathOrText/RefChip completely unchanged.
function tokenizeMarked(blockText, marks) {
  const segments = segmentsWithActiveMarks(blockText, marks || []);
  return segments.map((seg) => ({ active: seg.active, tokens: tokenize(seg.text, false) }));
}

const SEMANTIC_TAG_FOR_KIND = { bold: 'strong', italic: 'em', underline: 'u', strike: 's', code: 'code' };
const SEMANTIC_KIND_ORDER = ['bold', 'italic', 'underline', 'strike', 'code'];
// Matches teacher-mobile's TEXT_COLORS/BG_COLORS — color/bg/equation marks
// have no semantic HTML equivalent, so they stay inline styles (BG_COLOR_HEX
// matches the desktop highlight palette mobile's own palette was updated to
// match, per Feature 3 of this same session).
const MARK_TEXT_COLOR_HEX = { red: '#EF4444', orange: '#F97316', yellow: '#D97706', green: '#16A34A', blue: '#2563EB', purple: '#7C3AED' };
const MARK_BG_COLOR_HEX = { yellow: '#FDE047', green: '#86EFAC', blue: '#93C5FD', pink: '#FCA5A5', purple: '#C4B5FD' };

function renderMarkedTokens(markedSegments, baseKey, references, wrapHighlights) {
  return markedSegments.map((seg, i) => {
    let node = renderTokens(seg.tokens, `${baseKey}-${i}`, wrapHighlights, references, false);

    const style = {};
    for (const m of seg.active) {
      if (m.kind === 'equation') { style.fontStyle = 'italic'; style.color = '#0D9488'; }
      if (m.kind === 'color' && m.param && MARK_TEXT_COLOR_HEX[m.param]) style.color = MARK_TEXT_COLOR_HEX[m.param];
      if (m.kind === 'bg' && m.param && MARK_BG_COLOR_HEX[m.param]) style.backgroundColor = MARK_BG_COLOR_HEX[m.param];
    }
    if (Object.keys(style).length > 0) node = <span style={style}>{node}</span>;

    for (const kind of SEMANTIC_KIND_ORDER) {
      if (seg.active.some((m) => m.kind === kind)) {
        const Tag = SEMANTIC_TAG_FOR_KIND[kind];
        node = <Tag>{node}</Tag>;
      }
    }

    return <React.Fragment key={`${baseKey}-s-${i}`}>{node}</React.Fragment>;
  });
}

function renderBlockScheme(text, references, wrapHighlights) {
  const blocks = parseBlocks(text, { legacyDollarToggle: false });
  const nodes = [];
  let i = 0;
  while (i < blocks.length) {
    const block = blocks[i];
    if (block.type === 'bullet' || block.type === 'numbered') {
      const listType = block.type;
      const group = [];
      while (i < blocks.length && blocks[i].type === listType) { group.push(blocks[i]); i++; }
      const Tag = listType === 'bullet' ? 'ul' : 'ol';
      nodes.push(
        <Tag key={`list-${nodes.length}`} className={`${listType === 'bullet' ? 'list-disc' : 'list-decimal'} pl-5 space-y-1 mt-3`}>
          {group.map((b, j) => (
            <li key={j}>{renderMarkedTokens(tokenizeMarked(b.text, b.marks), `li-${nodes.length}-${j}`, references, wrapHighlights)}</li>
          ))}
        </Tag>
      );
      continue;
    }
    const content = renderMarkedTokens(tokenizeMarked(block.text, block.marks), `blk-${nodes.length}`, references, wrapHighlights);
    if (block.type === 'h1') nodes.push(<h1 key={nodes.length} className="text-2xl font-extrabold text-gray-900 mt-4 first:mt-0">{content}</h1>);
    else if (block.type === 'h2') nodes.push(<h2 key={nodes.length} className="text-xl font-extrabold text-gray-900 mt-4 first:mt-0">{content}</h2>);
    else if (block.type === 'h3') nodes.push(<h3 key={nodes.length} className="text-lg font-bold text-gray-900 mt-3 first:mt-0">{content}</h3>);
    else if (block.type === 'h4') nodes.push(<h4 key={nodes.length} className="text-base font-bold text-gray-900 mt-3 first:mt-0">{content}</h4>);
    else if (block.type === 'quote') nodes.push(<blockquote key={nodes.length} className="border-l-2 border-gray-300 pl-3 italic text-gray-500 mt-3">{content}</blockquote>);
    else nodes.push(<p key={nodes.length} className={nodes.length > 0 ? 'mt-4' : ''}>{content}</p>);
    i++;
  }
  return nodes;
}

/**
 * Inline variant of RenderContent for the short strings inside cards, list
 * items, flashcards, problems, objectives and takeaways. Renders math tokens
 * (inline chips + standalone block math) and soft-strips markdown, but emits
 * no wrapper element of its own, so it can sit inside any existing <p>/<span>
 * without changing the layout. Both the teacher Content Editor and the
 * student viewer use this so card content renders identically on both sides.
 */
export function MathText({ text, references }) {
  if (text === null || text === undefined || text === '') return null;
  const tokens = tokenize(String(text));
  return <>{renderTokens(tokens, 'mt', null, references)}</>;
}

// Segments already inside $$...$$, \[...\], \(...\) or $...$ must never be
// touched by the "bare equation" auto-wrap heuristic below — its lhs/rhs
// match is non-greedy and can land *inside* an already-delimited span
// (e.g. matching "F = m \cdot" within "$F = m \cdot a$"), which previously
// produced corrupted double-wraps like "$$F = m \cdot$ a$".
const EXISTING_MATH_SPLIT = /(\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\]|\\\([^\n]+?\\\)|\$[\s\S]+?\$)/g;

function autoWrapBareEquations(text) {
  return text
    .split(EXISTING_MATH_SPLIT)
    .map((segment, i) => {
      if (i % 2 === 1) return segment; // an already-delimited math span — leave untouched
      return segment
        .replace(/\\cdota/g, '\\cdot a')
        .replace(/\b([A-Za-z][A-Za-z0-9]?)\s*=\s*([A-Za-z0-9][^\n$]{0,40}?[A-Za-z0-9\)])\b/g, (_m, lhs, rhs) => `$${lhs} = ${rhs}$`);
    })
    .join('');
}

export function RenderContent({ text, className = '', highlights, references, markdown = false }) {
  const paragraphs = useMemo(() => {
    if (!text) return [];
    const eqNormalized = String(text)
      .replace(/<eq([^>])/g, '<eq>$1')
      .replace(/<\/eq([^>])/g, '</eq>$1')
      .replace(/<eq>([\s\S]*?)<\/eq>/g, (_m, inner) => `$${inner}$`);
    const cleaned = autoWrapBareEquations(eqNormalized)
      .replace(/^#+\s*(.*)$/gm, markdown ? '**$1**' : '$1')
      .replace(/^[-*+]\s+/gm, '  \u2022  ')
      .replace(/^\d+\.\s+/gm, (m) => `  ${m.trim()}  `)
      .replace(/_{2,}/g, '')
      .replace(/^\s*>\s?/gm, '')
      .replace(/\n{3,}/g, '\n\n')
      .replace(/[\t ]{2,}/g, ' ')
      .replace(/([a-zA-Z0-9])\n\1/g, '$1')
      .replace(/([a-zA-Z0-9])(\n[a-zA-Z0-9])+/g, (match) => match.replace(/\n/g, ''))
      // Rich-editor escapes (\* \` \~ \<) in content with no other editor
      // markers land here, not in the block renderer that would unescape
      // them — show the plain character (and give KaTeX `<`, not `\<`).
      .replace(/\\([*`~<])/g, '$1')
      .trim();
    return cleaned.split(/\n\n+/);
  }, [text, markdown]);

  if (!text) return null;

  const isBlockScheme = !markdown && isNewSchemeContent(String(text));

  // Highlight matching substrings inside a rendered string — but only the
  // ONE occurrence the teacher actually selected (`h.occurrenceIndex`), not
  // every place that substring appears in the section.
  //
  // Each highlight is matched against the ORIGINAL, unmodified `node` text
  // independently — never against text already carved up by a previous
  // highlight. Highlights can nest or sit adjacent to each other (e.g. "h"
  // inside "he" inside "the"); slicing the string as each highlight is
  // applied would let an earlier highlight's cut sever a later highlight's
  // substring in two, making it unfindable. Matching everyone against the
  // pristine text and merging the resulting ranges at the end avoids that.
  //
  // `highlightSeen` tracks, per highlight id, how many occurrences of its
  // text have already been scanned across this whole render pass — shared
  // by reference across every wrapHighlights call, and paragraphs/tokens
  // render in document order, so the running count lines up with how the
  // teacher's selection offset was computed at creation time.
  const highlightSeen = {};
  const wrapHighlights = (node, keyPrefix) => {
    if (!highlights || highlights.length === 0 || typeof node !== 'string') return node;

    const ranges = []; // { start, end, hl }
    for (const h of highlights) {
      if (!h?.text) continue;
      const target = h.occurrenceIndex || 0;
      let start = 0;
      while (true) {
        const idx = node.indexOf(h.text, start);
        if (idx === -1) break;
        const seen = highlightSeen[h.id] || 0;
        highlightSeen[h.id] = seen + 1;
        if (seen === target) ranges.push({ start: idx, end: idx + h.text.length, hl: h });
        start = idx + h.text.length;
      }
    }
    if (ranges.length === 0) return node;

    // Two highlights can't both own the same characters — first (by
    // position) wins; later overlapping ranges are dropped.
    ranges.sort((a, b) => a.start - b.start);
    const accepted = [];
    let cursor = 0;
    for (const r of ranges) {
      if (r.start < cursor) continue;
      accepted.push(r);
      cursor = r.end;
    }

    const segments = [];
    let pos = 0;
    for (const r of accepted) {
      if (r.start > pos) segments.push({ text: node.slice(pos, r.start), hl: null });
      segments.push({ text: node.slice(r.start, r.end), hl: r.hl });
      pos = r.end;
    }
    if (pos < node.length) segments.push({ text: node.slice(pos), hl: null });

    return segments.map((p, i) =>
      p.hl
        ? <mark key={`${keyPrefix}-hl-${i}`} style={{ backgroundColor: p.hl.color, borderRadius: '2px', padding: '0 2px' }}>{p.text}</mark>
        : <React.Fragment key={`${keyPrefix}-t-${i}`}>{p.text}</React.Fragment>
    );
  };

  return (
    <div className={className}>
      {isBlockScheme ? renderBlockScheme(String(text), references, wrapHighlights) : paragraphs.map((para, i) => {
        const lines = para.split('\n');
        const isList = lines.every(l => l.trim().startsWith('\u2022') || l.trim() === '');
        if (isList && lines.filter(l => l.trim()).length > 0) {
          return (
            <ul key={i} className="space-y-1.5 my-3">
              {lines.filter(l => l.trim()).map((line, j) => {
                const cleaned = line.replace(/^\s*\u2022\s*/, '').trim();
                const tokens = tokenize(cleaned, markdown);
                return (
                  <li key={j} className="flex items-start gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-current opacity-40 mt-2.5 flex-shrink-0" />
                    <span>{renderTokens(tokens, `${i}-${j}`, wrapHighlights, references, markdown)}</span>
                  </li>
                );
              })}
            </ul>
          );
        }
        const tokens = tokenize(para, markdown);
        // If the whole paragraph is a single block-math token, render its
        // bordered card directly (no extra <div> wrapper that would break
        // line-height inheritance).
        if (tokens.length === 1 && tokens[0].type === 'math' &&
            (tokens[0].value.startsWith('$$') || tokens[0].value.startsWith('\\['))) {
          return (
            <React.Fragment key={i}>
              <MathOrText token={tokens[0].value} body={tokens[0].value} />
            </React.Fragment>
          );
        }
        return (
          <p key={i} className={i > 0 ? 'mt-4' : ''}>{renderTokens(tokens, `p-${i}`, wrapHighlights, references, markdown)}</p>
        );
      })}
    </div>
  );
}

/**
 * Strip markdown/math for previews & search snippets.
 */
export function cleanText(text) {
  if (!text) return '';
  return String(text)
    .replace(/\$\$[\s\S]+?\$\$/g, '[math]')
    .replace(/\\\[[\s\S]+?\\\]/g, '[math]')
    .replace(/\\\(([^\n]+?)\\\)/g, '$1')
    .replace(/\$([^\n$]+?)\$/g, '$1')
    .replace(/\[ref:\d+\]/g, '')
    .replace(/^#+\s*/gm, '')
    .replace(/\*\*\*(.*?)\*\*\*/g, '$1')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^[-*+]\s+/gm, '\u2022 ')
    .replace(/_{2,}/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '')
    .replace(/^\s*>\s?/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export default RenderContent;
